import { cva } from 'class-variance-authority';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useClickOutside } from 'hooks';
import { cn } from 'utils';
import { Button } from '../Button';
import Label from '../Label';
import CustomCalendar from './CustomCalendar';
import { CalendarValue, CalendarValuePiece } from './types';

import { CalendarOutlineIcon } from 'assets/svgs';

interface Props {
  className?: string;
  label: string;
  labelTooltip?: string;
  bgColor?: 'layer1' | 'layer2' | 'layer3';
  value: string;
  placeholder?: string;
  setValue: Dispatch<SetStateAction<string>>;
}

const colorVariants = cva('', {
  variants: {
    bgColor: {
      layer1: 'bg-bg-layer1',
      layer2: 'bg-bg-layer2',
      layer3: 'bg-bg-layer3'
    }
  }
});

const DatePicker = ({ className, label, labelTooltip, bgColor = 'layer1', placeholder, value, setValue }: Props) => {
  const [rawValue, setRawValue] = useState<CalendarValue>([new Date(), new Date()]);
  const [date, setDate] = useState<string>(dayjs().format('YYYY/MM/DD'));
  const [time, setTime] = useState<string>('00:00:00');
  const [isCalendarShown, setIsCalendarShown] = useState<boolean>(false);
  const { ref } = useClickOutside(onClickOutside);

  function onClickTopButton() {
    setIsCalendarShown(true);
  }

  function onClickOutside() {
    setIsCalendarShown(false);
  }

  function onRestart() {
    setRawValue([new Date(), new Date()]);
  }

  function onCancel() {
    setRawValue([new Date(), new Date()]);
    setIsCalendarShown(false);
  }

  function onApply() {
    setValue(`${date} ${time}`);
    setIsCalendarShown(false);
  }

  useEffect(() => {
    const rawValueAsSingleValue = rawValue as CalendarValuePiece;
    setDate(dayjs(rawValueAsSingleValue).format('YYYY-MM-DD'));
  }, [rawValue]);

  return (
    <div className={classnames(className)}>
      <Label label={label} labelTooltip={labelTooltip} />
      <div
        className={colorVariants({
          bgColor,
          className: classnames('border-border-primary', 'relative h-8 w-full rounded border')
        })}
        ref={ref}
      >
        <button
          type="button"
          onClick={onClickTopButton}
          className="flex h-full w-full flex-row items-center justify-between gap-2 pl-3 pr-1"
        >
          <span
            className={cn('inline-block flex-1 text-left !text-body2 font-regular', {
              'text-text-tertiary': !value,
              'text-text-primary': value
            })}
          >
            {value || placeholder}
          </span>
          <div className="flex h-6 w-6 items-center justify-center">
            <CalendarOutlineIcon className="h-3 w-3 " />
          </div>
        </button>
        {isCalendarShown && (
          <div
            className={classnames('absolute z-10 mt-1 flex flex-col border', 'border-border-primary shadow-primary')}
          >
            <CustomCalendar
              rawValue={rawValue}
              setRawValue={setRawValue}
              dateValue={date}
              timeValue={time}
              setTimeValue={setTime}
            />

            <div className="flex w-full flex-row items-center justify-between bg-bg-layer1 p-4">
              <Button className="h-8" text="Restart" onClick={onRestart} type="tertiary" size="large" />
              <div className="flex flex-row gap-1">
                <Button className="h-8" text="Cancel" onClick={onCancel} type="transparent" size="large" />
                <Button className="h-8" text="Apply" onClick={onApply} type="blue" size="large" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatePicker;
